import { useCallback, useMemo, useRef, useState } from "react";

import { useLanguage } from "~/contexts/providers/language";
import { useGrabToScroll } from "~/hooks/grabToScroll";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { EventType } from "~/types/event/event.types";
import { filterDays } from "~/utils/constants/days";
import { formatNumber } from "~/utils/functions/string.functions";

import CannotBookPopup from "../cannotBookPopup";
import Event from "../memberEvent";
import styles from "./index.module.scss";

type Props = {
  weekHeader: string;
  data: EventType[];
};

const WeekScroller = ({ weekHeader, data }: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [cannotBookPopup, setCannotBookPopup] = useState(false);
  const [days, setDays] = useState<number[]>([]);

  const { language } = useLanguage();
  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;

  useGrabToScroll(scrollContainerRef, {
    grabCursor: true,
  });

  const displayWeekEvents = useMemo(() => {
    return data
      .filter((el) => {
        const dateFrom = new Date(el.dateFrom);
        const dateTo = new Date(el.dateTo);
        return (
          days.length === 0 ||
          days.includes(dateFrom.getDay()) ||
          days.includes(dateTo.getDay())
        );
      })
      .sort((a, b) => (new Date(a.dateFrom) < new Date(b.dateFrom) ? -1 : 1))
      .map((event) => (
        <Event
          key={event.id}
          event={event}
          readonly={true}
          setCannotBookPopup={setCannotBookPopup}
        />
      ));
  }, [data, days]);

  const handleSetDay = useCallback((day: number) => {
    setDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((el) => el !== day);
      }
      return [...prev, day];
    });
  }, []);

  return (
    <div className={styles.container}>
      <CannotBookPopup open={cannotBookPopup} setOpen={setCannotBookPopup} />
      <div className={styles.weekInfo}>
        <div className={styles.weekPeriod}>{weekHeader}</div>
        <div>
          {`${formatNumber(data.length)} ${accountTexts.event}${
            data.length > 1 ? "s" : ""
          }`}
        </div>
        <div className={styles.weekDays}>
          {filterDays.map(({ value, textEn, textFr }) => (
            <span
              key={value}
              onClick={handleSetDay.bind(this, value)}
              className={days.includes(value) ? styles.active : undefined}
            >
              {language === "En" ? textEn : textFr}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.weekEvents} ref={scrollContainerRef}>
        {displayWeekEvents}
      </div>
    </div>
  );
};

export default WeekScroller;
